import {
  type CallHomeAuthorizeResult,
  type CallHomeAwsSettings,
  type CallHomeCookie,
  type CallHomeUser,
  type Eula,
  type JWTUser,
} from '@solinkcorp/authentication';
import { type CallHomeClientSettings, type CallHomeServiceMap } from '@solinkcorp/vd-common';
import { jwtDecode } from 'jwt-decode';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { S3 } from 'utils/S3';

/**
 * Wrapper helper class to represent the Auth Session
 */
export default class AuthSession {
  authToken: string;
  profile: CallHomeUser;
  clientSettings: CallHomeClientSettings;
  aws?: CallHomeAwsSettings;
  cookies?: CallHomeCookie[];
  authTokenPayload: JWTUser;
  expiresAt: number;
  eula: Eula;
  widgetMetadata?: any;
  billingMetadata?: {
    stripeCustomerId?: string;
  };
  serviceMap: CallHomeServiceMap;
  biAccessToken?: string;

  private _s3: S3 | undefined;
  private _firebaseToken?: string;

  constructor({
    accessToken: authToken,
    aws,
    clientSettings,
    cookies = [],
    firebaseToken,
    profile,
    eula,
    widgetMetadata,
    billingMetadata,
    serviceMap,
    biAccessToken,
  }: CallHomeAuthorizeResult) {
    this.authToken = authToken;

    // only if there is aws credentials (Not a Guest)
    if (aws && !isEmpty(aws)) {
      this.aws = aws;
      this._s3 = new S3(this.aws);
    }

    this.profile = profile;
    this.cookies = cookies;
    this.clientSettings = clientSettings;

    this.authTokenPayload = jwtDecode(authToken);
    this.expiresAt = this.authTokenPayload.exp * 1000;

    this.billingMetadata = billingMetadata;
    this.firebaseToken = firebaseToken;
    this.eula = eula;
    this.widgetMetadata = widgetMetadata;
    this.serviceMap = serviceMap;
    this.biAccessToken = biAccessToken;
  }

  /**
   * Sets the firebase token of the user
   * @returns {string}
   */
  set firebaseToken(token: string | undefined) {
    this._firebaseToken = token;
  }

  /**
   * Return the firebase token of the user
   * @returns {string}
   */
  get firebaseToken() {
    return this._firebaseToken;
  }

  /**
   * Return the email of the user
   * @returns {string}
   */
  get email() {
    return this.profile.email;
  }

  /**
   * Gets the id of the user
   * @returns {string}
   */
  get userId() {
    return this.profile.id;
  }

  /**
   * Get the user type
   * @returns {string}
   */
  get userType() {
    return this.profile.userType;
  }

  /**
   * Get the user identity type
   * @returns {string}
   */
  get identityType() {
    return this.profile.identityType;
  }

  /**
   * Check if MFA is enabled for the user
   * @returns {boolean}
   */
  get isMfaEnabled() {
    return this.profile.isMfaEnabled;
  }

  /**
   * Set MFA enabled for the user
   * @param {boolean} isEnabled
   */
  set isMfaEnabled(isEnabled: boolean | undefined) {
    this.profile.isMfaEnabled = isEnabled;
  }

  /**
   * Get the videoPlaybackStartsAt as a Date
   * @returns {Date|undefined}
   */
  get videoPlaybackStartsAtDate(): Date | null {
    // ISO String
    const dateStr = get(this, 'profile.metadata.videoPlaybackStartsAt');
    if (dateStr) {
      const videoPlaybackStartsAtDate = new Date(dateStr);
      if (videoPlaybackStartsAtDate instanceof Date) {
        return videoPlaybackStartsAtDate;
      }
    }
    return null;
  }

  /**
   * Get the videoPlaybackEndsDate as a Date
   * @returns {Date|undefined}
   */
  get videoPlaybackEndsDate(): Date | null {
    // ISO String
    const dateStr = get(this, 'profile.metadata.videoPlaybackEndsAt');
    if (dateStr) {
      const videoPlaybackEndsDate = new Date(dateStr);
      if (videoPlaybackEndsDate instanceof Date) {
        return videoPlaybackEndsDate;
      }
    }
    return null;
  }

  /**
   * Get the metadata
   * @returns {object}
   */
  get metadata() {
    return this.profile.metadata;
  }

  /**
   * Get the tenant id of the user
   * @returns {string}
   */
  get tenantId() {
    return this.profile.customerId;
  }

  /**
   * Get the reseller id of the user
   * @returns {string}
   */
  get resellerId() {
    return this.profile.resellerId;
  }

  /**
   * Get the cloud id of the user
   * @returns {string}
   */
  get cloudId() {
    return this.profile.cloudId;
  }

  /**
   * Get the id of the role for this user
   * @returns {string}
   */
  get userRoleId() {
    return this.profile.userRoleId;
  }

  /**
   * Get the AWS S3 Client
   */
  get s3(): S3 {
    if (!this._s3) {
      throw new Error(`This user does not support an S3 Client: user=${this}`);
    }
    return this._s3;
  }

  /**
   * Get string representation
   * @returns {string}
   */
  toString() {
    return `${this.email}: [userType=${this.userType}, expiresAt=${new Date(
      this.expiresAt,
    ).toISOString()}]`;
  }
}
